import type { FormDetail, Intake } from '@atlas-control/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IntakeEditor } from '@/features/forms/intake-editor';
import { api } from '@/lib/api';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/forms/new')({
  beforeLoad: ({ context }) => guard(context.me, 'forms.create'),
  component: NewFormPage,
});

const CONTACT_FIELDS = [
  ['name', 'Name', 'text'],
  ['email', 'Email', 'email'],
  ['phone', 'Phone', 'tel'],
  ['jobTitle', 'Job title', 'text'],
] as const;

/** A form entered by hand for a custom run (admins and developers). */
function NewFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [contact, setContact] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: (intake: Intake) => {
      const typed = Object.fromEntries(
        Object.entries(contact)
          .filter(([, v]) => v.trim())
          .map(([k, v]) => [k, v.trim()]),
      );
      return api<{ form: FormDetail }>('/forms', {
        method: 'POST',
        body: { intake, contact: typed },
      });
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      toastSuccess('Form created');
      void navigate({ to: '/forms/$formId', params: { formId: res.form.id } });
    },
    onError: (err) => toastError(err),
  });

  return (
    <>
      <PageHeader title="New form" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Intake</CardTitle>
          </CardHeader>
          <CardContent>
            <IntakeEditor
              intake={{}}
              saving={create.isPending}
              submitLabel="Create form"
              onCancel={() => void navigate({ to: '/forms' })}
              onSave={(intake) => create.mutate(intake)}
            />
          </CardContent>
        </Card>
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-base">Contact</CardTitle>
            <CardDescription>Optional; can be filled in later.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {CONTACT_FIELDS.map(([key, label, type]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`c-${key}`}>{label}</Label>
                <Input
                  id={`c-${key}`}
                  type={type}
                  value={contact[key] ?? ''}
                  onChange={(e) => setContact((c) => ({ ...c, [key]: e.target.value }))}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
